import React, { useState, useContext, useEffect, useCallback } from 'react';
import styles from './styles.module.css';
import { Grid, Skeleton } from '@mui/material';
import StockReportService from '../../../services/StockReportService';
import { AlertMessagesContext } from 'react-alert-messages';
import ShopsHelper from '../../../helpers/ShopsHelper';
import { ShopContext } from '../../../Context/ShopContext';
import { useTranslation } from 'react-i18next';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';

function StockSummaryReport() {
  const { t: tCommon } = useTranslation("common");
  const { t: tReport } = useTranslation("report");
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { shop } = useContext(ShopContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [positiveStockSummary, setPositiveStockSummary] = useState([]);
  const [negativeStockSummary, setNegativeStockSummary] = useState([]);

  const loadStockSummaryReports = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const resp = await StockReportService.getStockSummaryReport();
      setPositiveStockSummary(resp.positive_stock_summary);
      setNegativeStockSummary(resp.negative_stock_summary);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  }, [postAlertMessage]);

  useEffect(() => {
    loadStockSummaryReports();
  }, [loadStockSummaryReports]);

  const _getStockReportSummary = (items) => {
    let totalCost = 0;
    let totalSaleValue = 0;
    let totalItemCount = 0;
    items.forEach((item) => {
      totalCost += item.total_cost;
      totalSaleValue += item.total_sale_value;
      totalItemCount += item.item_count;
    });

    return {
      totalCost: ShopsHelper.getAmountFormatted(shop, totalCost),
      totalSaleValue: ShopsHelper.getAmountFormatted(shop, totalSaleValue),
      totalItemCount: totalItemCount,
    };
  };

  const summary = _getStockReportSummary([...positiveStockSummary, ...negativeStockSummary]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon("Reports")}<span className={styles.menuTitle}>{tCommon("Generation")}</span>
        </span>
      </div>
      <div className={styles.changeable} style={{ marginTop: '20px', padding: '15px' }}>
        <div className={styles.filterSec} style={{ marginTop: '10px' }}>
          <div className={styles.headTitle}>
            <h2 className={styles.subHeader}>{tReport("Stock Summary Report")}</h2>
          </div>
        </div>
        <div className={styles.reportSec}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {tCommon("Total Cost")}
            </Grid>
            <Grid item xs={6}>
              {loadingIndicator ? <Skeleton variant="text" animation="wave" /> : summary.totalCost}
            </Grid>
            <Grid item xs={6}>
              {tCommon("Total Sale Value")}
            </Grid>
            <Grid item xs={6}>
              {loadingIndicator ? <Skeleton variant="text" animation="wave" /> : summary.totalSaleValue}
            </Grid>
            <Grid item xs={6}>
              {tCommon("Total Inventory Items")}
            </Grid>
            <Grid item xs={6}>
              {loadingIndicator ? <Skeleton variant="text" animation="wave" /> : summary.totalItemCount}
            </Grid>
          </Grid>
        </div>
      </div>
      <div className={styles.changeable} style={{ marginTop: '20px', padding: '15px' }}>
        <div className={styles.filterSec} style={{ marginTop: '10px' }}>
          <div className={styles.headTitle}>
            <h2 className={styles.subHeader}>{tReport("Positive Stock Unit Wise Summary")}</h2>
          </div>
        </div>
        <div className={styles.reportSec}>
          <Grid container spacing={2}>
            {loadingIndicator ? (
              <Grid item xs={12}><Skeleton variant="rectangular" height={100} animation="wave" /></Grid>
            ) : positiveStockSummary.map((item) => (
              <>
                <Grid item xs={12} style={{ padding: '25px 5px 10px' }}>
                  {tCommon("Unit")} - {item.unit ? String(item.unit).toUpperCase() : 'N/A'}
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: '25px' }}>
                  {tCommon("Total Cost")}
                </Grid>
                <Grid item xs={6}>
                  {item.total_cost}
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: '25px' }}>
                  {tCommon("Total Sale Value")}
                </Grid>
                <Grid item xs={6}>
                  {item.total_sale_value}
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: '25px' }}>
                  {tCommon("Total Items")}
                </Grid>
                <Grid item xs={6}>
                  {item.item_count}
                </Grid>
              </>
            ))}
          </Grid>
        </div>
      </div>
      <div className={styles.changeable} style={{ marginTop: '20px', padding: '15px' }}>
        <div className={styles.filterSec} style={{ marginTop: '10px' }}>
          <div className={styles.headTitle}>
            <h2 className={styles.subHeader}>{tReport("Negative Stock Unit Wise Summary")}</h2>
          </div>
        </div>
        <div className={styles.reportSec}>
          <Grid container spacing={2}>
            {loadingIndicator ? (
              <Grid item xs={12}><Skeleton variant="rectangular" height={100} animation="wave" /></Grid>
            ) : negativeStockSummary.map((item) => (
              <>
                <Grid item xs={12} style={{ padding: '25px 5px 10px' }}>
                  {tCommon("Unit")} - {item.unit ? String(item.unit).toUpperCase() : 'N/A'}
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: '25px' }}>
                  {tCommon("Total Cost")}
                </Grid>
                <Grid item xs={6}>
                  {item.total_cost}
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: '25px' }}>
                  {tCommon("Total Sale Value")}
                </Grid>
                <Grid item xs={6}>
                  {item.total_sale_value}
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: '25px' }}>
                  {tCommon("Total Items")}
                </Grid>
                <Grid item xs={6}>
                  {item.item_count}
                </Grid>
              </>
            ))}
          </Grid>
        </div>
      </div>
    </div>
  );
}

export default withConsoleBase(StockSummaryReport);
